import { Inject, Injectable } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	Resolve,
	RouterStateSnapshot,
} from '@angular/router';
import { Observable } from 'rxjs';
import { BrLazyComponents, ComponentConfigDefinition } from '@app/features';
import { environment } from '@env/environment';
import { HttpClient } from '@angular/common/http';
import { WINDOW } from './shared/utilities/window';

@Injectable({ providedIn: 'root' })
export class ComponentConfigResolver implements Resolve<ComponentConfigDefinition> {
	private urlCache = new Map<string, Record<string, any>>();
	private accumulatedMapping: Record<string, any> = {};

	constructor(private http: HttpClient, @Inject(WINDOW) readonly windowRef: Window) {}

	resolve(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	): Observable<ComponentConfigDefinition> | Promise<ComponentConfigDefinition> | ComponentConfigDefinition {
		if (this.isInsideBloomreach()) {
			return this.createPreviewMappingFromLazyLoadedComponents(BrLazyComponents);
		} else {
			return this.createMappingFromLazyLoadedComponents(BrLazyComponents, state);
		}
	}

	private getPageCtypes(stateUrl: string): Promise<string[]> {
		return new Promise((resolve) => {
			const url = environment.cmsBaseUrl + '/resourceapi' + stateUrl;
			this.http.get(url).subscribe({
				next: (resource: any) => {
					const cKey: string[] = [];
					for (const key in resource.page) {
						const ctype = resource?.page[key]?.ctype;
						if (ctype) cKey.push(ctype);
					}
					resolve(cKey);
				},
				error: () => resolve([]),
			});
		});
	}

	private async createMappingFromLazyLoadedComponents(
		lazyComponentConfig: ComponentConfigDefinition,
		state: any
	): Promise<Record<string, any>> {
		// Strip query params — component structure is path-based, not query-based
		const cacheKey = state.url.split('?')[0];

		if (this.urlCache.has(cacheKey)) {
			return this.urlCache.get(cacheKey)!;
		}

		const cKey = await this.getPageCtypes(state.url);

		// Seed with all previously loaded components so no existing component ever
		// disappears from the mapping mid-navigation (prevents the "not defined" flash)
		const mapping: Record<string, any> = { ...this.accumulatedMapping };
		const seen = new Set<string>();
		const tasks: Promise<void>[] = [];

		for (let i = 0; i < cKey.length; i++) {
			const key = cKey[i];
			if (seen.has(key)) continue;
			seen.add(key);

			const config = lazyComponentConfig[key];

			if (!config || typeof config.moduleImport !== 'function') {
				if (!environment.production) {
					console.warn(`[LazyLoader] Skipping '${key}': missing or invalid config`);
				}
				continue;
			}

			const task = config.moduleImport().then(async (module) => {
				let resolved: any;

				if (typeof config.componentImport === 'function') {
					const compModule = await config.componentImport();
					resolved = compModule[config.componentName!];

					if (!resolved && !environment.production) {
						console.warn(`[LazyLoader] '${config.componentName}' not found in componentImport for '${key}'`);
					}
				} else {
					resolved = module;
				}

				if (resolved) {
					mapping[key] = resolved;
					this.accumulatedMapping[key] = resolved;
				}
			});

			tasks.push(task);
		}

		await Promise.all(tasks);
		this.urlCache.set(cacheKey, mapping);
		return mapping;
	}

	isInsideBloomreach(): boolean {
		try {
			const inIframe = this.windowRef?.self !== this.windowRef?.top;
			const href = this.windowRef?.location?.href;
			const looksLikeBrxm = href?.includes('cms') || href?.includes('experience-manager') || href?.includes('-preview');
			return inIframe || looksLikeBrxm;
		} catch {
			// Cross-origin iframe -> definitely inside BRXM
			return true;
		}
	}

	private async createPreviewMappingFromLazyLoadedComponents(
		lazyComponentConfig: ComponentConfigDefinition
	): Promise<Record<string, any>> {
		const componentKeys = Object.keys(lazyComponentConfig);
		const mapping: Record<string, any> = {};
		const tasks: Promise<void>[] = [];

		for (const key of componentKeys) {
			const config = lazyComponentConfig[key];
			if (!config || typeof config?.moduleImport !== 'function') continue;

			const task = config?.moduleImport()?.then(async (module) => {
				let resolved: any;

				if (typeof config?.componentImport === 'function') {
					const compModule = await config?.componentImport();
					resolved = compModule[config?.componentName!];
				} else {
					resolved = module;
				}

				if (resolved) {
					mapping[key] = resolved;
				}
			});

			tasks.push(task);
		}

		await Promise.all(tasks);
		return mapping;
	}
}
